import * as React from 'react';
import classNames from 'classnames';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { NewModal } from './NewModal';

interface WizardModalProps {
	isOpen: boolean;
	label: string;
	title: string;
	step: number;
	totalSteps: number;
	/** Replaces "Step n of N" — e.g. once the flow has finished. */
	stepLabel?: string;
	onClose: () => void;
	children: React.ReactNode;
	footer: React.ReactNode;
	modalSize?: 'sm' | 'md' | 'lg' | 'xl';
	className?: string;
	shouldCloseOnOverlayClick?: boolean;
	shouldCloseOnEsc?: boolean;
}

/**
 * Modal that reuses NewModal's shell but renders a step-progress header. Used by the
 * cancellation wizard; other multi-step flows can adopt the same component.
 */
export function WizardModal({
	isOpen,
	label,
	title,
	step,
	totalSteps,
	stepLabel,
	onClose,
	children,
	footer,
	modalSize = 'md',
	className,
	shouldCloseOnOverlayClick = false,
	shouldCloseOnEsc = true,
}: WizardModalProps) {
	const percent = Math.max(0, Math.min(100, (step / totalSteps) * 100));

	return (
		<NewModal
			isOpen={isOpen}
			label={label}
			modalSize={modalSize}
			onRequestClose={onClose}
			shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
			shouldCloseOnEsc={shouldCloseOnEsc}
			className={classNames('wizard-modal', className)}
		>
			<NewModal.Header className="wizard-modal__header">
				<div className="flex items-start justify-between gap-3 px-6 pt-5 pb-4">
					<div className="min-w-0">
						<h4 className="m-0 text-xl font-bold text-slate-900 leading-tight">{title}</h4>
						<div className="text-[11px] text-slate-500 font-semibold uppercase tracking-widest mt-1">
							{stepLabel ?? `Step ${step} of ${totalSteps}`}
						</div>
					</div>
					<button
						type="button"
						className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors"
						onClick={onClose}
						aria-label="Close"
					>
						<Icon name="close" useMaterialIcons />
					</button>
				</div>
				<div className="h-1 w-full bg-slate-100 overflow-hidden">
					<div
						className="h-full bg-sky-500 transition-all duration-300 ease-out"
						style={{ width: `${percent}%` }}
					/>
				</div>
			</NewModal.Header>

			<NewModal.Body className="wizard-modal__body">
				<div className="p-6">{children}</div>
			</NewModal.Body>

			<NewModal.Footer className="wizard-modal__footer">{footer}</NewModal.Footer>
		</NewModal>
	);
}
